import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";

// Pages
import PhoneLogin from "./components/auth/PhoneLogin";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import Khata from "./pages/Khata";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Team from "./pages/Team";
import Inventory from "./pages/Inventory";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/20"></div>
                    <div className="w-32 h-4 bg-muted rounded"></div>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Public Route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/20"></div>
                    <div className="w-32 h-4 bg-muted rounded"></div>
                </div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <PhoneLogin />
                    </PublicRoute>
                } 
            />
            
            {/* Protected Routes */}
            <Route 
                path="/" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/transactions" 
                element={
                    <ProtectedRoute>
                        <Transactions />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/transactions/:id" 
                element={
                    <ProtectedRoute>
                        <Transactions />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/khata" 
                element={
                    <ProtectedRoute>
                        <Khata />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/team" 
                element={
                    <ProtectedRoute>
                        <Team />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/inventory" 
                element={
                    <ProtectedRoute>
                        <Inventory />
                    </ProtectedRoute>
                } 
            />
            
            {/* Public Page - Privacy Policy */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AuthProvider>
                    <NotificationProvider>
                        <BrowserRouter>
                            <AppRoutes />
                            <Toaster position="top-center" richColors />
                        </BrowserRouter>
                    </NotificationProvider>
                </AuthProvider>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
